import {
  type ObjectResultsTableColumnId,
  type SearchResultItem,
} from '@bizzdesign/data-access-search';
import {
  type AttributeDataPayload,
  type ObjectSearchResultDto,
} from '@bizzdesign/shared/models';
import { attributeDtoToAttributeDataPayload } from '@bizzdesign/shared/utils';
import {
  createAttributeColumnId,
  type ObjectResultsColumnCatalogue,
} from './object-results-columns.utils';

/**
 * The attribute values of the loaded rows, keyed by row and then by column.
 */
export type ObjectResultsAttributeValues = ReadonlyMap<
  SearchResultItem['id'],
  ReadonlyMap<ObjectResultsTableColumnId, AttributeDataPayload>
>;

/**
 * Narrows a search result to its object case.
 *
 * @param rawResult - The raw search result a row carries, if any.
 * @returns The result as an object result, or `undefined` for a row of another
 * scope or a row carrying no raw result.
 *
 * @remarks
 * The one place that asserts the metamodel. Narrowing on `scope` alone leaves
 * the result typed over every metamodel, while everything downstream — the
 * object type, and the attributes inside it — is typed on `BDCore`, which is
 * the only metamodel the search runs on. Asserting the object type on its own
 * would not do: the attributes would stay generic.
 */
function toObjectSearchResult(
  rawResult: SearchResultItem['rawResult'],
): ObjectSearchResultDto | undefined {
  if (rawResult?.scope !== 'OBJECT') {
    return undefined;
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- the search runs on BDCore alone, but SearchResultDto is typed over every metamodel
  return rawResult as ObjectSearchResultDto;
}

/**
 * Collects the attribute values the loaded rows carry, ready for the results
 * table to render.
 *
 * @param items - The rows currently loaded.
 * @param catalogue - The columns on offer for the current search.
 * @returns The values of every row that has any, keyed the same way as the
 * column ids so a cell costs one lookup instead of a nested scan per render.
 *
 * @remarks
 * Attribute values reach the table inside `rawResult`, so this is where the
 * search result is narrowed to its object case, rather than leaving the untyped
 * table template to reach through the scope union.
 *
 * Values the catalogue has no column for are skipped, and on a repeated key the
 * first value wins, matching how the details drawer reads a list-valued
 * attribute.
 */
export function createObjectResultsAttributeValues(
  items: readonly SearchResultItem[],
  catalogue: ObjectResultsColumnCatalogue,
): ObjectResultsAttributeValues {
  const valuesByItemId = new Map<
    SearchResultItem['id'],
    Map<ObjectResultsTableColumnId, AttributeDataPayload>
  >();
  for (const item of items) {
    const objectResult = toObjectSearchResult(item.rawResult);
    if (!objectResult) {
      continue;
    }
    const values = new Map<ObjectResultsTableColumnId, AttributeDataPayload>();
    for (const category of objectResult.attributeCategories) {
      for (const attribute of category.attributes) {
        const columnId = createAttributeColumnId(category.id, attribute.id);
        const column = catalogue.byId.get(columnId);
        if (column?.kind !== 'attribute' || values.has(columnId)) {
          continue;
        }
        values.set(
          columnId,
          attributeDtoToAttributeDataPayload(
            attribute,
            objectResult.type,
            column.attributeDefinition,
          ),
        );
      }
    }
    valuesByItemId.set(item.id, values);
  }
  return valuesByItemId;
}
